import React, { useContext, useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// CONTEXT
import { ThemeContext } from '../context/Themes';
import { SidebarContext } from '../context/Sidebar';
import { TranslatorContext } from '../context/Translator';

// COMPONENTS
import { Link } from 'react-router-dom';
import { Dropdown } from "react-bootstrap";

// DATA
import orders from "../assets/data/orders.json";
import messages from "../assets/data/messages.json";
import languages from "../assets/data/languages.json";
import notifications from "../assets/data/notifications.json";

export default function HeaderLayout() {

    const { theme, toggleTheme } = useContext(ThemeContext);
    const { sidebar, toggleSidebar } = useContext(SidebarContext);
    const { t, n, c, changeLanguage, currentLanguage } = useContext(TranslatorContext);

    const searchRef = useRef();
    const searchInputRef = useRef();
    const searchContainerRef = useRef();

    const [scroll, setScroll] = useState("fixed");
    const [search, setSearch] = useState(false); // Changed to boolean
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState({
        customers: [],
        pets: []
    });
    const [searchLoading, setSearchLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const navigate = useNavigate();

    const [user, setUser] = useState(null);

    useEffect(() => {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
            setUser(JSON.parse(storedUser));
        }
    }, []);

    // Debounced search
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (searchQuery.length > 0) {
                performSearch();
                setShowDropdown(true);
            } else {
                setSearchResults({ customers: [], pets: [] });
                setShowDropdown(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [searchQuery]);

    // Scroll event listener with cleanup
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 0) setScroll("sticky");
            else setScroll("fixed");
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Click outside listener with cleanup
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setSearch(false);
                setShowDropdown(false);
                setSearchResults({ customers: [], pets: [] });
                setSearchQuery("");
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const performSearch = async () => {
        if (!searchQuery.trim()) {
            return;
        }

        setSearchLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            const apiUrl = `${process.env.REACT_APP_API_URL}/search`;

            console.log("Searching for:", searchQuery);
            console.log("API URL:", apiUrl);

            const response = await axios.get(apiUrl, {
                params: { query: searchQuery },
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            console.log("Search response:", response.data);

            if (response.data) {
                setSearchResults({
                    customers: response.data.customers || [],
                    pets: response.data.pets || []
                });
                setShowDropdown(true);
            }
        } catch (error) {
            console.error("Search error:", error);
            setSearchResults({ customers: [], pets: [] });
        } finally {
            setSearchLoading(false);
        }
    };

    const handleResultClick = (customerId) => {
        navigate(`/customers-profile/${customerId}`);
        setSearch(false);
        setShowDropdown(false);
        setSearchResults({ customers: [], pets: [] });
        setSearchQuery("");
    };

    // Inline styles
    const styles = {
        searchWrapper: {
            position: 'relative',
            display: 'inline-block',
        },
        searchContainer: {
            position: 'absolute',
            top: '100%',
            left: '0',
            right: '0',
            marginTop: '8px',
            zIndex: '9999',
        },
        searchDropdown: {
            minWidth: '307px',
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
        },
        sectionHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            background: '#f8f9fa',
            borderBottom: '1px solid #e9ecef',
        },
        sectionHeaderIcon: {
            fontSize: '18px',
            color: '#0d6efd',
        },
        sectionHeaderText: {
            fontSize: '13px',
            fontWeight: '600',
            color: '#212529',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            margin: 0,
        },
        resultItem: {
            cursor: 'pointer',
            borderBottom: '1px solid #f0f0f0',
        },
        resultContent: {
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            textDecoration: 'none',
            color: 'inherit',
        },
        resultAvatar: {
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            overflow: 'hidden',
            flexShrink: '0',
        },
        resultImage: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
        },
        resultInfo: {
            flex: '1',
        },
        resultName: {
            fontWeight: '500',
            color: '#212529',
            marginBottom: '4px',
            fontSize: '14px',
        },
        resultDetail: {
            fontSize: '12px',
            color: '#6c757d',
            margin: 0,
        },
        noResults: {
            textAlign: 'center',
            padding: '40px 20px',
        },
        noResultsIcon: {
            fontSize: '48px',
            color: '#6c757d',
            marginBottom: '16px',
            opacity: '0.5',
        },
        noResultsText: {
            color: '#212529',
            marginBottom: '8px',
            fontWeight: '500',
        },
        noResultsSmall: {
            color: '#6c757d',
            fontSize: '12px',
        },
        loader: {
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            border: '2px solid #e9ecef',
            borderTop: '2px solid #0d6efd',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
        },
        searchInput: {
            width: '300px',
        }
    };

    // Add keyframes animation
    useEffect(() => {
        const styleSheet = document.createElement("style");
        styleSheet.textContent = `
            @keyframes spin {
                0% { transform: translateY(-50%) rotate(0deg); }
                100% { transform: translateY(-50%) rotate(360deg); }
            }
            .search-result-item:hover {
                background: #f8f9fa !important;
            }
            .mc-header-search-group {
                position: relative !important;
            }
        `;
        document.head.appendChild(styleSheet);
        return () => {
            document.head.removeChild(styleSheet);
        };
    }, []);

    return (
        <header className={`mc-header ${scroll}`}>
            <Link to='/dashboard' className='mc-logo-group'>
                <img src='/images/logo.png' alt='logo' />
            </Link>
            <div className='mc-header-group'>
                <div className='mc-header-left'>

                    <button type='button' className='mc-header-icon toggle' onClick={toggleSidebar}>
                        <i className="material-icons">{sidebar ? "menu_open" : "menu"}</i>
                    </button>

                    {/* Search Input */}
                    {search && (
                        <div style={styles.searchWrapper}>
                            <form className="mc-header-search" ref={searchRef} onSubmit={(e) => e.preventDefault()}>
                                <input
                                    ref={searchInputRef}
                                    type="search"
                                    placeholder={t('quick_finding') + '...'}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={styles.searchInput}
                                    autoFocus
                                />
                                {searchLoading && <div style={styles.loader}></div>}
                            </form>

                            {/* Search Results Dropdown */}
                            {showDropdown && (
                                <div style={styles.searchContainer}>
                                    <div className="mc-dropdown-paper" style={styles.searchDropdown}>
                                        {(searchResults.customers.length > 0 || searchResults.pets.length > 0) ? (
                                            <div className="mc-header-dropdown-group">
                                                <div className="mc-card-header">
                                                    <h4 className="mc-card-title">
                                                        {t('search results') || 'Search Results'} ({searchResults.customers.length + searchResults.pets.length})
                                                    </h4>
                                                </div>

                                                <ul className="mc-header-dropdown-list thin-scrolling" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                    {searchResults.customers.length > 0 && (
                                                        <>
                                                            <li style={styles.sectionHeader}>
                                                                <i className="material-icons" style={styles.sectionHeaderIcon}>people</i>
                                                                <span style={styles.sectionHeaderText}>{t('customers') || 'Customers'} ({searchResults.customers.length})</span>
                                                            </li>
                                                            {searchResults.customers.map((customer) => (
                                                                <li
                                                                    key={`customer-${customer.id}`}
                                                                    className="search-result-item"
                                                                    style={styles.resultItem}
                                                                    onClick={() => handleResultClick(customer.id)}
                                                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                                                >
                                                                    <div style={styles.resultContent}>

                                                                        <div style={styles.resultInfo}>
                                                                            <div style={styles.resultName}>{customer.name}</div>
                                                                            <div style={styles.resultDetail}>
                                                                                {customer.email && <span>{customer.email}</span>}
                                                                                {customer.email && customer.phone && <span> • </span>}
                                                                                {customer.phone && <span>{customer.phone}</span>}
                                                                                {!customer.email && !customer.phone && <span>No contact info</span>}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                            ))}
                                                        </>
                                                    )}

                                                    {searchResults.pets.length > 0 && (
                                                        <>
                                                            <li style={styles.sectionHeader}>
                                                                <i className="material-icons" style={styles.sectionHeaderIcon}>pets</i>
                                                                <span style={styles.sectionHeaderText}>{t('pets') || 'Pets'} ({searchResults.pets.length})</span>
                                                            </li>
                                                            {searchResults.pets.map((pet) => (
                                                                <li
                                                                    key={`pet-${pet.id}`}
                                                                    className="search-result-item"
                                                                    style={styles.resultItem}
                                                                    onClick={() => handleResultClick(pet.customer_id)}
                                                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                                                >
                                                                    <div style={styles.resultContent}>

                                                                        <div style={styles.resultInfo}>
                                                                            <div style={styles.resultName}>{pet.name}</div>
                                                                            <div style={styles.resultDetail}>
                                                                                {pet.breed?.name || 'Pet'} • Owner: {pet.customer_name || 'Unknown'}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                            ))}
                                                        </>
                                                    )}
                                                </ul>
                                            </div>
                                        ) : (
                                            searchQuery.length > 0 && !searchLoading && (
                                                <div className="mc-header-dropdown-group">
                                                    <div className="mc-card-header">
                                                        <h4 className="mc-card-title">
                                                            {t('no_results') || 'No Results Found'}
                                                        </h4>
                                                    </div>
                                                    <div style={styles.noResults}>
                                                        <i className="material-icons" style={styles.noResultsIcon}>search_off</i>
                                                        <p style={styles.noResultsText}>{t('no_results_found') || 'No customers or pets found'}</p>
                                                        <small style={styles.noResultsSmall}>Try searching by name, email, or phone</small>
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <button
                        type='button'
                        className='mc-header-icon search'
                        style={{ display: 'block ' }}
                        onClick={() => {
                            setSearch(!search);
                            if (!search) {
                                setTimeout(() => searchInputRef.current?.focus(), 100);
                            }
                        }}
                    >
                        <i className="material-icons">search</i>
                    </button>
                </div>

                {/* Rest of your header right content - unchanged */}
                <div className='mc-header-right'>
                    {/* ... all your existing dropdowns ... */}
                    <button type='button' className='mc-header-icon theme' onClick={toggleTheme}>
                        <i className="material-icons">{theme}</i>
                    </button>

                    {/* LANGUAGE PART */}
                    {/* <Dropdown>
                        <Dropdown.Toggle className='mc-dropdown-toggle mc-header-icon language'>
                            <i className='material-icons'>language</i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end" className="mc-dropdown-paper">
                            {languages.map((language, index) => (
                                <button onClick={() => changeLanguage(language.code)} type='button' key={index} className="mc-header-language">
                                    <img src={language.flag} alt="language" />
                                    <span>{language.name}</span>
                                    {language.code === currentLanguage.code && <i className="material-icons">done</i>}
                                </button>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown> */}

                    {/* ORDER PART */}
                    {/* <Dropdown className='order'>
                        <Dropdown.Toggle className="mc-dropdown-toggle mc-header-icon">
                            <i className='material-icons'>shopping_cart</i>
                            <sup className='primary'>{n(12)}</sup>
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end" className="mc-dropdown-paper">
                            <div className="mc-header-dropdown-group">
                                <div className="mc-card-header">
                                    <h4 className="mc-card-title">
                                        {t('orders') + ' '}
                                        ({n(12)})
                                    </h4>
                                    <Dropdown bsPrefix="mc-dropdown">
                                        <Dropdown.Toggle bsPrefix="mc-dropdown-toggle">
                                            <i className='material-icons'>settings</i>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu align="end" className="mc-dropdown-paper">
                                            <button type='button' className='mc-dropdown-menu'><i className='material-icons'>drafts</i><span>{t('mark_all_as_read')}</span></button>
                                            <button type='button' className='mc-dropdown-menu'><i className='material-icons'>markunread</i><span>{t('mark_all_as_unread')}</span></button>
                                            <button type='button' className='mc-dropdown-menu'><i className='material-icons'>delete</i><span>{t('delete_all_order')}</span></button>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                                <ul className="mc-header-dropdown-list thin-scrolling">
                                    {orders?.map((order, index) => (
                                        <li key={index} className={`mc-header-dropdown-item`}>
                                            <Link to='#' className='mc-header-dropdown-content'>
                                                <div className="mc-header-dropdown-shop-media">
                                                    {order?.product?.images.map((image, index) => (
                                                        <img key={index} src={image} alt="product" />
                                                    ))}
                                                    <span>+{n(order?.product?.count)}</span>
                                                </div>
                                                <div className="mc-header-dropdown-meta">
                                                    <h4>
                                                        <cite>{order?.name}</cite>
                                                        <time>{order?.shortMoment}</time>
                                                    </h4>
                                                    <p>({c(order?.price) + ' '}) {t('total_price')}</p>
                                                </div>
                                            </Link>
                                            <Dropdown bsPrefix="mc-dropdown">
                                                <Dropdown.Toggle bsPrefix="mc-dropdown-toggle">
                                                    <i className='material-icons'>more_vert</i>
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu align="end" className="mc-dropdown-paper">
                                                    <button type='button' className='mc-dropdown-menu'><i className='material-icons'>account_circle</i><span>{t('view_profile')}</span></button>
                                                    <button type='button' className='mc-dropdown-menu'><i className='material-icons'>mark_chat_read</i><span>{t('mark_as_unread')}</span></button>
                                                    <button type='button' className='mc-dropdown-menu'><i className='material-icons'>delete</i><span>{t('delete_order')}</span></button>
                                                    <button type='button' className='mc-dropdown-menu'><i className='material-icons'>remove_circle</i><span>{t('block_order')}</span></button>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </li>
                                    ))}
                                </ul>
                                <Link to='#' className='mc-btn primary mc-header-dropdown-button'>
                                    {t('view_all_orders')}
                                </Link>
                            </div>
                        </Dropdown.Menu>
                    </Dropdown> */}

                    {/* MESSAGE PART */}
                    {/* <Dropdown className='message'>
                        <Dropdown.Toggle className="mc-dropdown-toggle mc-header-icon">
                            <i className='material-icons'>email</i>
                            <sup className='primary'>{n(23)}</sup>
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end" className="mc-dropdown-paper">
                            <div className="mc-header-dropdown-group">
                                <div className="mc-card-header">
                                    <h4 className="mc-card-title">
                                        {t('messages') + ' '}
                                        ({n(23)})
                                    </h4>
                                    <Dropdown bsPrefix="mc-dropdown">
                                        <Dropdown.Toggle bsPrefix="mc-dropdown-toggle">
                                            <i className='material-icons'>settings</i>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu align="end" className="mc-dropdown-paper">
                                            <button type='button' className='mc-dropdown-menu'><i className='material-icons'>drafts</i><span>{t('mark_all_as_read')}</span></button>
                                            <button type='button' className='mc-dropdown-menu'><i className='material-icons'>markunread</i><span>{t('mark_all_as_unread')}</span></button>
                                            <button type='button' className='mc-dropdown-menu'><i className='material-icons'>delete</i><span>{t('delete_all_message')}</span></button>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                                <ul className="mc-header-dropdown-list thin-scrolling">
                                    {messages?.map((message, index) => (
                                        <li key={index} className={`mc-header-dropdown-item`}>
                                            <Link to='#' className='mc-header-dropdown-content'>
                                                <div className="mc-header-dropdown-message-media">
                                                    <div className='mc-round-avatar xs online'>
                                                        <img src={message?.user?.image} alt='avatar' />
                                                    </div>
                                                </div>
                                                <div className="mc-header-dropdown-meta">
                                                    <h4>
                                                        <cite>{message?.name}</cite>
                                                        <time>{message?.shortMoment}</time>
                                                    </h4>
                                                    <p>{message?.chat}</p>
                                                </div>
                                            </Link>
                                            {message?.badge && <sup>{n(message?.badge)}</sup>}
                                            <Dropdown bsPrefix="mc-dropdown">
                                                <Dropdown.Toggle bsPrefix="mc-dropdown-toggle">
                                                    <i className='material-icons'>more_vert</i>
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu align="end" className="mc-dropdown-paper">
                                                    <button type='button' className='mc-dropdown-menu'><i className='material-icons'>account_circle</i><span>{t('view_profile')}</span></button>
                                                    <button type='button' className='mc-dropdown-menu'><i className='material-icons'>mark_chat_read</i><span>{t('mark_as_unread')}</span></button>
                                                    <button type='button' className='mc-dropdown-menu'><i className='material-icons'>delete</i><span>{t('delete_message')}</span></button>
                                                    <button type='button' className='mc-dropdown-menu'><i className='material-icons'>remove_circle</i><span>{t('block_message')}</span></button>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </li>
                                    ))}
                                </ul>
                                <Link to='#' className='mc-btn primary mc-header-dropdown-button'>
                                    {t('view_all_messages')}
                                </Link>
                            </div>
                        </Dropdown.Menu>
                    </Dropdown> */}

                    {/* NOTIFICATION PART */}
                    {/* <Dropdown className='notify'>
                        <Dropdown.Toggle className="mc-dropdown-toggle mc-header-icon">
                            <i className='material-icons'>notifications</i>
                            <sup className='primary'>{n(34)}</sup>
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end" className="mc-dropdown-paper">
                            <div className="mc-header-dropdown-group">
                                <div className="mc-card-header">
                                    <h4 className="mc-card-title">
                                        {t('notifications') + ' '}
                                        ({n(34)})
                                    </h4>
                                    <Dropdown bsPrefix="mc-dropdown">
                                        <Dropdown.Toggle bsPrefix="mc-dropdown-toggle">
                                            <i className='material-icons'>settings</i>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu align="end" className="mc-dropdown-paper">
                                            <button type='button' className='mc-dropdown-menu'><i className='material-icons'>drafts</i><span>{t('mark_all_as_read')}</span></button>
                                            <button type='button' className='mc-dropdown-menu'><i className='material-icons'>markunread</i><span>{t('mark_all_as_unread')}</span></button>
                                            <button type='button' className='mc-dropdown-menu'><i className='material-icons'>delete</i><span>{t('delete_all_message')}</span></button>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                                <ul className="mc-header-dropdown-list thin-scrolling">
                                    {notifications?.map((notification, index) => (
                                        <li key={index} className={`mc-header-dropdown-item ${notification.isActive && 'active'}`}>
                                            <Link to='#' className='mc-header-dropdown-content'>
                                                <div className="mc-header-dropdown-notify-media">
                                                    <img src={notification?.notify?.image} alt="avatar" />
                                                    <i className={`material-icons ${notification?.notify?.variant}`}>{notification?.notify?.icon}</i>
                                                </div>
                                                <div className="mc-header-dropdown-meta">
                                                    <h4>
                                                        <span dangerouslySetInnerHTML={{ __html: notification?.note }} />
                                                    </h4>
                                                    <p>{notification?.longMoment}</p>
                                                </div>
                                            </Link>
                                            <Dropdown bsPrefix="mc-dropdown">
                                                <Dropdown.Toggle bsPrefix="mc-dropdown-toggle">
                                                    <i className='material-icons'>more_vert</i>
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu align="end" className="mc-dropdown-paper">
                                                    <button type='button' className='mc-dropdown-menu'><i className='material-icons'>mark_chat_read</i><span>{t('mark_as_unread')}</span></button>
                                                    <button type='button' className='mc-dropdown-menu'><i className='material-icons'>delete</i><span>{t('delete_notification')}</span></button>
                                                    <button type='button' className='mc-dropdown-menu'><i className='material-icons'>remove_circle</i><span>{t('block_notification')}</span></button>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </li>
                                    ))}
                                </ul>
                                <Link to='#' className='mc-btn primary mc-header-dropdown-button'>
                                    {t('view_all_notifications')}
                                </Link>
                            </div>
                        </Dropdown.Menu>
                    </Dropdown> */}

                    {/* PROFILE PART */}
                    <Dropdown className="mc-header-user">
                        <Dropdown.Toggle className="mc-dropdown-toggle">
                            <Link to='#' className='mc-round-avatar xs'>
                                <img
                                    src={
                                        user?.profile_image
                                            ? `${process.env.REACT_APP_IMG_URL}${user.profile_image}`
                                            : "/images/avatar/01.webp"
                                    }
                                    alt='avatar'
                                />
                            </Link>
                            <div className='mc-duel-text xs'>
                                <h3 className="mc-duel-text-title">
                                    {user?.name || "Guest User"}
                                </h3>
                                <p className="mc-duel-text-descrip">
                                    {user?.email || "guest@example.com"}
                                </p>
                            </div>
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end" className="mc-dropdown-paper">
                            <Link to='/my-account' className='mc-dropdown-menu'><i className='material-icons'>person</i><span>{t('my_account')}</span></Link>
                            <Link to='/forgot-password' className='mc-dropdown-menu'><i className='material-icons'>privacy_tip</i><span>{t('reset_password')}</span></Link>
                            <Link to='/login' className='mc-dropdown-menu'><i className='material-icons'>lock</i><span>{t('logout')}</span></Link>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>
        </header>
    );
}